<template>
    <div class="c-radio-button" :class="{ 'c-radio-button--light': isLight }">
        <Field
            :id="safeId"
            :name="name"
            :aria-label="labelText"
            :aria-required="isRequired"
            :disabled="isDisabled"
            :tabindex="tabIndex"
            :value="modelValue"
            :modelValue="checkedValue"
            :form="formTarget"
            :checked="checked"
            :rules="{ required: isRequired }"
            type="radio"
            :class="{ 'c-radio-button__input': true, 'c-radio-button__input--checked': checked }"
            @change="onRadioButtonChange"
        />
        <slot
            :id="safeId"
            :isSelected="checked"
            :labelText="labelText"
            :onChange="onRadioButtonChange"
            :value="modelValue"
        >
            <label :for="safeId" class="c-radio-button__label o-text--body-copy" @click.stop>
                {{ labelText }}
            </label>
        </slot>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { uniqueId } from 'lodash-es';

import formFieldProps from '~coreModules/core/js/ui/formFieldProps';
import { Field } from 'vee-validate';

const props = defineProps({
    ...formFieldProps,
    // Only used for v-model, not intended to be passed in
    checkedValue: {
        type: [String, Boolean, Number],
        default: null,
    },
    isLight: {
        type: [Boolean, String],
        default: false,
    },
    modelValue: {
        type: [String, Boolean, Number],
        default: null,
    },
    formTarget: {
        type: String,
        default: null,
    },
});

const emit = defineEmits(['update:checkedValue']);

const checked = computed(() => props.checkedValue === props.modelValue);

const safeId = computed(() => {
    const uuid = uniqueId();
    return props.id ? props.id : `radio-input-uid-${uuid}`;
});

function onRadioButtonChange() {
    emit('update:checkedValue', props.modelValue);
}
</script>

<style lang="scss">
.c-radio-button {
    $this: &;

    position: relative;
    display: flex;
    justify-content: flex-start;
    overflow: hidden;

    &__label,
    &__label::before,
    &__input {
        transition-property: border-color, color, background;
        transition-timing-function: ease;
        transition-duration: 0.2s;
        cursor: pointer;
    }

    &--light {
        #{$this}__label {
            color: $nu-gray;

            &:hover,
            &:focus,
            &:active,
            &--checked ~ #{$this}__label {
                color: $nu-black;
            }

            &::before {
                background-color: $nu-white;
                border-color: $nu-white;
            }
        }
    }

    &__input {
        position: absolute;
        left: -999999px;

        &[data-focus-visible-added]:not(:disabled) ~ #{$this}__label::before {
            outline: $focus-outline;
            outline-offset: -4px;
        }

        &:hover,
        &:focus,
        &:active {
            &:not(#{$this}--checked):not(:disabled) ~ #{$this}__label {
                color: $nu-black;

                &::before {
                    border-color: $nu-primary;
                }
            }
        }

        &:disabled ~ #{$this}__label {
            color: $nu-gray--medium;

            &::before {
                background: $transparent;
                border-color: $nu-gray--light;
            }
        }

        &--checked ~ #{$this}__label {
            color: $nu-black;

            &::before {
                background-color: $nu-white;
                border-color: $nu-primary;
                border-width: 8px;
            }
        }
    }

    &__label {
        $radio-button-width: 24px;

        color: $nu-gray;
        padding-left: calc(#{$nu-spacer-2} + #{$radio-button-width});
        text-transform: none;

        &::before {
            box-sizing: border-box;
            background-color: $nu-gray--light;
            border: 1px solid $nu-gray--light;
            border-radius: 100%;
            content: ' ';
            display: inline-block;
            height: 24px;
            position: absolute;
            left: 0;
            top: 0;
            width: $radio-button-width;
        }
    }
}
</style>
